import { request, requestBlob, saveBlob } from './core'
import type { AssessmentMeta, AssessmentState, AssessmentSummary } from './types'

export const assessmentsApi = {
  listAssessments: () => request<AssessmentSummary[]>('/api/assessments'),

  createAssessment: (params: {
    display_name: string
    company_url?: string | undefined
    research?: boolean | undefined
  }) =>
    request<{ slug: string; collision_resolved: boolean; state: AssessmentState }>(
      '/api/assessments',
      { method: 'POST', body: JSON.stringify(params) },
    ),

  getAssessment: (slug: string) =>
    request<AssessmentState>(`/api/assessments/${slug}`),

  /** Revision + last-writer + current viewers. Polled by the status banner;
   *  each call also touches presence for the signed-in caller. */
  getAssessmentMeta: (slug: string) =>
    request<AssessmentMeta>(`/api/assessments/${slug}/meta`),

  /** Reassign the funding-program quarter this assessment is for. Optimistic
   *  retry server-side (a field edit, never a 412). */
  putAssessmentPeriod: (slug: string, period: { year: number; quarter: number }) =>
    request<{ period_year: number; period_quarter: number; revision: number }>(
      `/api/assessments/${slug}/period`,
      {
        method: 'PUT',
        body: JSON.stringify({ period_year: period.year, period_quarter: period.quarter }),
      },
    ),

  deleteAssessment: (slug: string, revision?: number) =>
    request<{ deleted_objects: number }>(
      `/api/assessments/${slug}`,
      { method: 'DELETE' },
      { ifMatch: revision },
    ),

  /** Fetch state.json as a blob and trigger a browser download. */
  exportAssessment: async (slug: string): Promise<void> => {
    const blob = await requestBlob(`/api/assessments/${slug}/export`)
    saveBlob(blob, `${slug}-state.json`)
  },

  /** POST the FE-built intake markdown; backend renders it to .docx and we
   *  trigger a browser download. For sending the captured intake to a client. */
  /** `variant: 'questionnaire'` titles the cover "Questionnaire" and names the
   *  file `<slug>-questionnaire.docx` (the emailed fill-in version). */
  exportIntakeDocx: async (
    slug: string,
    markdown: string,
    variant: 'summary' | 'questionnaire' = 'summary',
  ): Promise<void> => {
    const blob = await requestBlob(`/api/assessments/${slug}/intake-export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ markdown, variant }),
    })
    saveBlob(blob, `${slug}-${variant === 'questionnaire' ? 'questionnaire' : 'intake'}.docx`)
  },

  /** Multipart upload of a state.json. Returns the slug the import landed at
   * (may differ from the file's `company_slug` if there was a collision). */
  importAssessment: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request<{ slug: string; collision_resolved: boolean; state: AssessmentState }>(
      '/api/assessments/import',
      { method: 'POST', body: fd },
    )
  },
}
